import type { StepId } from '@/types';
import { useT } from '@/i18n/runtime';
import { useSessionStore } from '@/store/sessionStore';
import { stepperPosition } from '../stepMachine';
import { stepLabelKey } from '../stepLabels';

interface StepperProps {
  current: StepId;
}

/**
 * Progress bar. The segment count + position derive from the store's
 * `stepperSteps` (KYCID-FSD-00013 §8) — plan-derived, so a check appearing or
 * disappearing re-indexes with no off-by-one. With no plan pinned this is the
 * legacy 6-segment stepper, unchanged.
 *
 * Item 7: the CURRENT step's human label renders beneath the bar (via the
 * shared `droom.check.*` / `droom.step.*` vocabulary in stepLabels.ts) — the
 * anonymous "3 / 6" alone told the customer nothing about what step 3 was.
 * A step with no known label (deploy skew) renders no label, never a raw key.
 */
export function Stepper({ current }: StepperProps): JSX.Element {
  const { t } = useT();
  const stepperSteps = useSessionStore((s) => s.stepperSteps);
  const total = stepperSteps.length;
  const position = stepperPosition(current, stepperSteps); // 1..total (0 for entry screens)
  const segs = Array.from({ length: total }, (_, i) => i + 1);
  const labelKey = stepLabelKey(current);
  return (
    <div>
      <div className="stepper">
        {segs.map((i) => {
          const state = i < position ? 'done' : i === position ? 'active' : '';
          return (
            <div key={i} className={`seg-bar ${state}`.trim()}>
              <i />
            </div>
          );
        })}
        <span className="step-meta">
          {Math.max(position, 1)} / {total}
        </span>
      </div>
      {labelKey ? <div className="step-label">{t(labelKey)}</div> : null}
    </div>
  );
}
